import type {
	GroupBase,
	OptionType,
} from '@/shared/components/refinement-bar/types'
import type { TFunction } from 'i18next'

import React from 'react'
import {
	entries,
	groupBy,
	map,
	orderBy,
	paths,
	pipe,
	prop,
	reduce,
	uniqBy,
} from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import {
	toCategoriesTree,
	useBrandsQuery,
	useCategoriesQuery,
} from '@/entities/core'
import { useOptimizationGroupsQuery } from '@/entities/optimization-group'
import {
	getAlertDataChoices,
	usePricingAlertsAnnotationsQuery,
} from '@/entities/price-alert'
import {
	EngineBadge,
	pcToOption,
	usePricingCampaignsQuery,
} from '@/entities/pricing-campaign'

import { Operators } from '@/shared/components/query-builder'
import {
	SelectFilter,
	TreeSelectFilter,
} from '@/shared/components/refinement-bar'
import { useOptimizationGroupId } from '@/shared/hooks/useOptimzationGroupId'
import { toOption } from '@/shared/tools/utils'

/**
 * Confoguration for specific filters that are not included in the schema
 */
export const getFieldConfig = (t: TFunction) => ({
	alerts: {
		label: t('field_schema_alerts', { ns: 'fields' }),
		type: SelectFilter,
		getInitialValue: () => ({
			operation: Operators.ANY_OF,
			value: [],
		}),
		useSelectOptions: () => {
			const { t } = useTranslation()

			return usePricingAlertsAnnotationsQuery({
				select: (data) => getAlertDataChoices(data, t),
			})
		},
	},
	optimization_group_id: {
		label: t('field_schema_optimization_group', {
			ns: 'fields',
		}),
		getInitialValue: () => ({
			operation: Operators.IN,
			value: [],
		}),
		type: SelectFilter,
		useSelectOptions: () =>
			useOptimizationGroupsQuery({
				select: pipe([map(paths(['id', 'name'])), toOption, uniqBy('value')]),
			}),
	},
	pricing_campaign_id: {
		label: t('field_schema_pricing_campaign', {
			ns: 'fields',
		}),
		getInitialValue: () => ({
			operation: Operators.IN,
			value: [],
		}),
		type: SelectFilter,
		useSelectOptions: () => {
			const optimizationGroupId = useOptimizationGroupId()
			const optimizationGroupsQuery = useOptimizationGroupsQuery<
				Record<string, string>
			>({
				select: reduce(
					(acc, group) => ({ ...acc, [group.id]: group.name }),
					{}
				),
			})
			const pricingCampaignsQuery = usePricingCampaignsQuery(
				optimizationGroupId
					? {
							optimization_group_id: optimizationGroupId,
						}
					: {}
			)
			const data = pipe([
				groupBy(prop('optimization_group_id')),
				entries,
				reduce(
					(acc, [id, list]) => [
						...acc,
						{
							label: optimizationGroupsQuery.data?.[id] || 'Glob',
							options: list.map(pcToOption),
						},
					],
					[] as GroupBase<OptionType>[]
				),
			])(pricingCampaignsQuery.data || [])

			return {
				isLoading:
					pricingCampaignsQuery.isLoading || optimizationGroupsQuery.isLoading,
				data,
			}
		},
		formatOptionLabel: (option: OptionType & { engine: string }) => (
			<>
				<div className='line-clamp-3'>{option.label}</div>
				<EngineBadge className='ml-auto' engine={option.engine}>
					{t(option.engine, { defaultValue: option.engine })}
				</EngineBadge>
			</>
		),
	},
	brands: {
		label: t('field_schema_brands', { ns: 'fields' }),
		type: SelectFilter,
		getInitialValue: () => ({
			operation: Operators.ANY_OF,
			value: [],
		}),
		useSelectOptions: () => {
			const optimizationGroupId = useOptimizationGroupId()

			return useBrandsQuery(
				optimizationGroupId
					? { optimization_group_id: optimizationGroupId }
					: {},
				{
					select: pipe([
						map(paths(['id', 'name'])),
						toOption,
						orderBy(['label'], ['asc']),
						uniqBy('value'),
					]),
				}
			)
		},
	},
	category_ids: {
		label: t('field_schema_categories', {
			ns: 'fields',
		}),
		type: TreeSelectFilter,
		getInitialValue: () => ({
			operation: Operators.ANY_OF,
			value: [],
		}),
		useSelectOptions: () => {
			const optimizationGroupId = useOptimizationGroupId()

			return useCategoriesQuery(
				optimizationGroupId
					? {
							optimization_group_id: optimizationGroupId,
						}
					: {},
				{ select: toCategoriesTree }
			)
		},
	},
})
